// Pinterest pin sirasi: Etsy'de yayina cikan listeler buraya duser.
// "Pinle" -> eklenti Pinterest'te formu doldurur, Yayinla'ya kullanici basar;
// yayin tespit edilince eklenti "pinlendi" isaretler.

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import { Pin, Loader2, RefreshCw, ExternalLink, Info, RotateCcw, Wand2, ChevronDown, ChevronUp } from "lucide-react";
import { pingExtension } from "../lib/extension";
import { useActiveShopId } from "../store/shop.store";
import { useShopVertical } from "../lib/useShopVertical";
import {
  getPinterestConfig, savePinterestConfig, listPinterestPins, updatePinterestPin, regeneratePinterestPin,
  backfillPinterestPins, fillPinViaExtension, pinImageSrc,
  type PinterestConfig, type PinterestConfigWithCategories, type PinterestPin, type PinStatus,
} from "../lib/pinterest";

const VERTICALS: Array<{ key: string; label: string; example: string }> = [
  { key: "apparel", label: "Tişört / Tekstil", example: "Personalized T-Shirts" },
  { key: "jewelry", label: "Takı", example: "Custom Name Jewelry" },
  { key: "giftware", label: "Hediyelik", example: "Personalized Gift Ideas" },
];

const TABS: Array<{ key: PinStatus; label: string }> = [
  { key: "queued", label: "Sırada" },
  { key: "pinned", label: "Pinlendi" },
  { key: "skipped", label: "Atlandı" },
];

const FIELD_LABELS: Record<string, string> = {
  image: "görsel", title: "başlık", description: "açıklama", link: "bağlantı", altText: "alt metin", board: "pano",
};

function SetupCard({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <button onClick={onToggle} className="flex w-full items-center justify-between px-4 py-3 text-left">
        <span className="font-semibold text-gray-900">Kurulum adımları</span>
        {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>
      {open && (
        <ol className="list-decimal space-y-2 border-t border-gray-100 px-4 py-3 pl-9 text-sm text-gray-700">
          <li>
            <a href="https://www.pinterest.com/business/create/" target="_blank" rel="noreferrer" className="text-orange-600 underline">
              Pinterest Business hesabı aç
            </a>{" "}
            (ücretsiz). Mevcut kişisel hesabın varsa ayarlardan Business'a çevirebilirsin. Hesap dilini İngilizce bırak; alıcıların ABD'de.
          </li>
          <li>
            Profil ayarlarında <b>Claimed accounts → Etsy</b> ile Etsy mağazanı sahiplen. Etsy linkli pinler sana atfedilir ve istatistiklerde görünür.
          </li>
          <li>
            Panoları açmana gerek yok: pano ürünün kategorisinden otomatik seçilir (Necklaces, Earrings, T-Shirts…) ve Pinterest'te
            yoksa eklenti pin sırasında oluşturur. Pano adlarını aşağıdan değiştirebilirsin.
          </li>
          <li>
            Eklentinin kurulu olduğu Chrome'da Pinterest'e giriş yap. Sonra <b>Pinle</b>'ye bas: eklenti formu doldurur, <b>Yayınla</b>'ya sen basarsın.
          </li>
          <li className="text-gray-500">
            Tempo: yeni hesapta günde 5–15 pin yeterli. Bir günde onlarca pin atmak spam gibi görünür.
          </li>
        </ol>
      )}
    </div>
  );
}

function SettingsCard({ cfg }: { cfg: PinterestConfigWithCategories }) {
  const qc = useQueryClient();
  const { vertical } = useShopVertical();
  const [form, setForm] = useState<PinterestConfig>(cfg);
  // Aktif magazanin dikeyindeki kategoriler
  const categories = cfg.categories.filter((c) => c.vertical === vertical);
  const [backfillN, setBackfillN] = useState(20);
  useEffect(() => setForm(cfg), [cfg]);

  const save = useMutation({
    mutationFn: () => savePinterestConfig(form),
    onSuccess: (d) => {
      qc.setQueryData(["pinterest-config"], { ...d, categories: cfg.categories });
      qc.invalidateQueries({ queryKey: ["pinterest-pins"] });
    },
  });
  const backfill = useMutation({
    mutationFn: () => backfillPinterestPins(backfillN),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pinterest-pins"] }),
  });

  return (
    <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4">
      <div>
        <h2 className="font-semibold text-gray-900">Panolar</h2>
        <p className="text-xs text-gray-500">
          {form.autoBoard
            ? "Pano, listenin başlığındaki ürün türünden seçilir. Pinterest'te yoksa eklenti oluşturur. Boş alan otomatik adı kullanır."
            : "Pin, listenin ürün grubuna göre bu panoya atılır. Boş grup varsayılan panoya düşer."}
        </p>
      </div>
      <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={form.autoBoard} onChange={(e) => setForm({ ...form, autoBoard: e.target.checked })} />
        Panoyu kategoriye göre otomatik seç
      </label>
      {form.autoBoard ? (
      <div className="grid gap-3 sm:grid-cols-2">
        {categories.map((c) => (
          <label key={c.key} className="text-sm">
            <span className="mb-1 block text-xs font-medium text-gray-600">{c.label}</span>
            <input
              value={form.categoryBoards[c.key] ?? ""}
              onChange={(e) => setForm({ ...form, categoryBoards: { ...form.categoryBoards, [c.key]: e.target.value } })}
              placeholder={c.board}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </label>
        ))}
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium text-gray-600">Kategori bulunamazsa</span>
          <input
            value={form.defaultBoard}
            onChange={(e) => setForm({ ...form, defaultBoard: e.target.value })}
            placeholder={vertical === "jewelry" ? "Jewelry" : vertical === "giftware" ? "Gift Ideas" : "Graphic Tees"}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
        </label>
      </div>
      ) : (
      <div className="grid gap-3 sm:grid-cols-2">
        {VERTICALS.map((v) => (
          <label key={v.key} className="text-sm">
            <span className="mb-1 block text-xs font-medium text-gray-600">{v.label}</span>
            <input
              value={form.boards[v.key] ?? ""}
              onChange={(e) => setForm({ ...form, boards: { ...form.boards, [v.key]: e.target.value } })}
              placeholder={v.example}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </label>
        ))}
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium text-gray-600">Varsayılan pano</span>
          <input
            value={form.defaultBoard}
            onChange={(e) => setForm({ ...form, defaultBoard: e.target.value })}
            placeholder="Gift Ideas"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
        </label>
      </div>
      )}
      <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={form.autoQueue} onChange={(e) => setForm({ ...form, autoQueue: e.target.checked })} />
        Etsy'de yayına çıkan listeyi otomatik sıraya ekle
      </label>
      <div className="flex items-center gap-3">
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50"
        >
          {save.isPending ? "Kaydediliyor…" : "Kaydet"}
        </button>
        {save.isSuccess && <span className="text-xs text-emerald-600">Kaydedildi</span>}
        {save.isError && <span className="text-xs text-red-600">{(save.error as Error).message}</span>}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3 text-sm text-gray-700">
        <span>Etsy'de zaten yayında olan son</span>
        <input
          type="number"
          min={1}
          max={300}
          value={backfillN}
          onChange={(e) => setBackfillN(Math.max(1, Math.min(300, Number(e.target.value) || 1)))}
          className="w-20 rounded-lg border border-gray-200 px-2 py-1 text-sm"
        />
        <span>listeyi sıraya ekle</span>
        <button
          onClick={() => backfill.mutate()}
          disabled={backfill.isPending}
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
        >
          {backfill.isPending ? "Ekleniyor…" : "Ekle"}
        </button>
        {backfill.isSuccess && <span className="text-xs text-emerald-600">{backfill.data} liste eklendi</span>}
        {backfill.isError && <span className="text-xs text-red-600">{(backfill.error as Error).message}</span>}
      </div>
    </div>
  );
}

function PinCard({ p, extReady }: { p: PinterestPin; extReady: boolean }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(p.title);
  const [description, setDescription] = useState(p.description);
  const [fillMsg, setFillMsg] = useState<{ text: string; tone: "ok" | "warn" | "err" } | null>(null);
  useEffect(() => { setTitle(p.title); setDescription(p.description); }, [p.title, p.description]);

  const refresh = () => qc.invalidateQueries({ queryKey: ["pinterest-pins"] });
  const patch = useMutation({
    mutationFn: (body: Parameters<typeof updatePinterestPin>[1]) => updatePinterestPin(p.id, body),
    onSuccess: refresh,
  });
  const regen = useMutation({ mutationFn: () => regeneratePinterestPin(p.id), onSuccess: refresh });
  const fill = useMutation({
    mutationFn: async () => {
      // Duzenlenmis metin once kaydedilir: eklenti icerigi sunucudan okur
      if (title !== p.title || description !== p.description) await updatePinterestPin(p.id, { title, description });
      return fillPinViaExtension(p.id);
    },
    onSuccess: (r) => {
      if (!r) setFillMsg({ text: "Eklentiye ulaşılamadı. Eklentinin kurulu ve güncel olduğundan emin ol.", tone: "err" });
      else if (r.error) setFillMsg({ text: r.error, tone: "err" });
      else if (r.missing.length) setFillMsg({ text: `Dolduruldu. Elle tamamla: ${r.missing.map((k) => FIELD_LABELS[k] ?? k).join(", ")}. Sonra Yayınla'ya bas.`, tone: "warn" });
      else setFillMsg({ text: "Dolduruldu. Pinterest sekmesinde kontrol edip Yayınla'ya bas.", tone: "ok" });
      refresh();
    },
  });

  const saveText = () => {
    if (title !== p.title || description !== p.description) patch.mutate({ title, description });
  };

  return (
    <div className="flex gap-4 rounded-xl border border-gray-200 bg-white p-4">
      <img src={pinImageSrc(p.imageUrl)} alt="" className="h-28 w-28 shrink-0 rounded-lg bg-gray-100 object-cover" />
      <div className="min-w-0 flex-1 space-y-2">
        {p.status === "queued" ? (
          <>
            <input
              value={title}
              maxLength={100}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={saveText}
              className="w-full rounded-lg border border-gray-200 px-2 py-1 text-sm font-medium"
            />
            <textarea
              value={description}
              maxLength={500}
              rows={3}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={saveText}
              className="w-full rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700"
            />
            <div className="text-right text-[10px] text-gray-400">{title.length}/100 · {description.length}/500</div>
          </>
        ) : (
          <>
            <div className="text-sm font-medium text-gray-900">{p.title}</div>
            <p className="line-clamp-2 text-xs text-gray-500">{p.description}</p>
          </>
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          <span className={p.resolvedBoard ? "text-gray-500" : "text-amber-700"}>
            Pano: {p.resolvedBoard || "eşleşme yok — yukarıdan pano ayarla"}
          </span>
          <a href={p.link} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-gray-500 hover:text-orange-600">
            Etsy <ExternalLink className="h-3 w-3" />
          </a>
          {p.pinUrl && (
            <a href={p.pinUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-red-600 hover:underline">
              Pin'i gör <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {p.pinnedAt && <span className="text-gray-400">{new Date(p.pinnedAt).toLocaleString("tr-TR")}</span>}
        </div>
        {p.note && p.status === "queued" && <p className="text-xs text-amber-700">{p.note}</p>}
        {fillMsg && (
          <p className={clsx("text-xs", fillMsg.tone === "ok" ? "text-emerald-700" : fillMsg.tone === "warn" ? "text-amber-700" : "text-red-600")}>
            {fillMsg.text}
          </p>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          {p.status === "queued" && (
            <>
              <button
                onClick={() => fill.mutate()}
                disabled={!extReady || fill.isPending}
                title={extReady ? "Pinterest sekmesinde formu doldur" : "Eklenti bağlı değil"}
                className="flex items-center gap-1.5 rounded-lg bg-[#e60023] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#c5001f] disabled:opacity-50"
              >
                {fill.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pin className="h-4 w-4" />} Pinle
              </button>
              <button
                onClick={() => patch.mutate({ status: "pinned" })}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
              >
                Pinlendi işaretle
              </button>
              <button
                onClick={() => patch.mutate({ status: "skipped" })}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
              >
                Atla
              </button>
              <button
                onClick={() => regen.mutate()}
                disabled={regen.isPending}
                title="Başlık ve açıklamayı Etsy listesinden yeniden üret"
                className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
              >
                <Wand2 className="h-3.5 w-3.5" /> Yeniden üret
              </button>
            </>
          )}
          {p.status !== "queued" && (
            <button
              onClick={() => patch.mutate({ status: "queued", pinUrl: null })}
              className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Sıraya geri al
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PinterestPage() {
  const [tab, setTab] = useState<PinStatus>("queued");
  // Sira aktif magazaya (ve dikeyine) gore suzulur
  const shopId = useActiveShopId();
  const { data: extVersion = null } = useQuery({ queryKey: ["extension-ping"], queryFn: pingExtension, staleTime: 60_000 });
  const { data: cfg } = useQuery({ queryKey: ["pinterest-config"], queryFn: getPinterestConfig });
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["pinterest-pins", shopId, tab],
    queryFn: () => listPinterestPins(tab),
    // Pin Pinterest sekmesinde yayinlaninca eklenti isaretler; sira kendiliginden guncellensin
    refetchInterval: tab === "queued" ? 15_000 : false,
  });
  const noBoards = !!cfg && !cfg.autoBoard && !cfg.defaultBoard && Object.keys(cfg.boards).length === 0;
  const [setupOpen, setSetupOpen] = useState(false);
  useEffect(() => { if (noBoards) setSetupOpen(true); }, [noBoards]);

  const pins = data?.data ?? [];
  const counts = data?.counts;

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <Pin className="h-6 w-6 text-[#e60023]" /> Pinterest
          </h1>
          <p className="mt-1 text-sm text-gray-500">Etsy'de yayına çıkan listeler pin sırasına düşer. Eklenti formu doldurur, Yayınla'ya sen basarsın.</p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw className={clsx("h-4 w-4", isFetching && "animate-spin")} /> Yenile
        </button>
      </div>

      <div className="flex gap-2 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-xs text-blue-800">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="space-y-2">
          <p>
            Pinterest API'si ayrı onay istediği için pinler <b>eklentiyle</b> atılır: Pinterest'in pin oluşturma sayfası açılır,
            görsel, başlık, açıklama, Etsy linki, alt metin ve pano doldurulur. Yayınladığında eklenti bunu fark edip pini
            "Pinlendi"ye taşır. Aynı işlem eklentinin yan panelindeki <b>Pinterest</b> sekmesinden de yapılır.
          </p>
          {extVersion ? (
            <span className="inline-block rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-800">Eklenti bağlı · v{extVersion}</span>
          ) : (
            <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800">
              Eklenti bağlı değil — Pinle çalışmaz (eklentiyi kurup sayfayı yenile)
            </span>
          )}
        </div>
      </div>

      <SetupCard open={setupOpen} onToggle={() => setSetupOpen((v) => !v)} />
      {cfg && <SettingsCard cfg={cfg} />}

      <div>
        <div className="mb-3 flex gap-1 border-b border-gray-200">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={clsx(
                "-mb-px border-b-2 px-3 py-2 text-sm font-medium",
                tab === t.key ? "border-orange-500 text-orange-600" : "border-transparent text-gray-500 hover:text-gray-800"
              )}
            >
              {t.label}
              {counts && <span className="ml-1.5 text-xs text-gray-400">{counts[t.key] ?? 0}</span>}
            </button>
          ))}
        </div>
        {isLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>
        ) : pins.length === 0 ? (
          <p className="py-10 text-center text-sm text-gray-400">
            {tab === "queued"
              ? "Sıra boş. Etsy'de yayına çıkan listeler buraya otomatik düşer; yayındakileri yukarıdan ekleyebilirsin."
              : "Kayıt yok."}
          </p>
        ) : (
          <div className="space-y-3">{pins.map((p) => <PinCard key={p.id} p={p} extReady={!!extVersion} />)}</div>
        )}
      </div>
    </div>
  );
}
